import PropTypes from 'prop-types';
import React, { Component } from 'react';
import injectSheet from 'react-jss';

import { UI_USER_IMPORT_WIZARD_TESTID } from '~/common/constants/testids/ui';
import { BulkImportUserResultType } from '~/enums/BulkImportUserResultType';
import { BulkImportUserSummaryType } from '~/enums/BulkImportUserSummaryType';
import { BulkImportUserToogleActionName } from '~/enums/BulkImportUserToogleActionName';
import { BulkImportUserType } from '~/enums/BulkImportUserType';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { injectIntl } from 'react-intl';
import FileInput from '~/ui/components/FileInput/FileInput';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Link from '~/ui/components/Link/Link';
import LoadingSpinner from '~/ui/components/LoadingSpinner/LoadingSpinner';
import Text from '~/ui/components/Text/Text';
import WizardPageLayout from '~/ui/components/WizardPageLayout/WizardPageLayout';

import styles from './BulkImportUserWizard.styles';
import BulkUserImportWarningModal from './BulkUserImportWarningModal/BulkUserImportWarningModal';
import { withBulkImportWarningModal } from './BulkUserImportWarningModal/withBulkImportWarningModal';
import SummaryTable from './SummaryTable/SummaryTable';

const steps = [BulkImportUserType.Upload, BulkImportUserType.Summary];

class BulkImportUserWizard extends Component {
  constructor(props) {
    super(props);

    this.toogleData = {
      [BulkImportUserToogleActionName.EmailNewUsers]: false,
      [BulkImportUserToogleActionName.UpdateExistingUsers]: false,
      [BulkImportUserToogleActionName.DeleteMissingUsers]: false,
    };

    this.state = {
      selectedFile: undefined,
      showWarningModal: false,
      warningModalProps: {},
      shouldSubtractMissingUsersFromTotal:
        !this.toogleData[BulkImportUserToogleActionName.DeleteMissingUsers],
      shouldSubtractUsersToUpdateFromTotal:
        !this.toogleData[BulkImportUserToogleActionName.UpdateExistingUsers],
    };
  }

  handleStartImportUserBulkProcessing = () => {
    this.props.onStartImportUserBulkProcessing(
      this.toogleData,
      this.handleHideWarningModal,
    );
  };

  handleShowWarningModal = () => {
    const { getBaseWarningModalProps } = this.props;
    const baseProps = getBaseWarningModalProps(this.toogleData);
    const warningModalProps = {
      ...baseProps,
      cancelAction: this.handleHideWarningModal,
      confirmAction: this.handleStartImportUserBulkProcessing,
    };
    this.setState({ showWarningModal: true, warningModalProps });
  };

  handleHideWarningModal = () => {
    this.setState({ showWarningModal: false });
  };

  handleUserListUploadActionClick = () => {
    this.props.onUploadActionClick(this.state.selectedFile);
  };

  handleDownloadUserTemplateClick = () => {
    const { handleDownloadUserTemplateFile } = this.props;

    handleDownloadUserTemplateFile(true);
  };

  handleSelectedFile = (files) => {
    this.setState({ selectedFile: files[0] });
  };

  handleImportResultsErrorFileDownloadClick = () => {
    const { handleImportResultsFileDownload } = this.props;
    const resultType = BulkImportUserResultType.SummaryErrors;

    handleImportResultsFileDownload(resultType);
  };

  isBulkUserImportFileAvailable = () => {
    return this.state.selectedFile;
  };

  updateToogleData = (importActions) => {
    this.toogleData = importActions;
    if (this.toogleData[BulkImportUserToogleActionName.UpdateExistingUsers]) {
      this.setState({ shouldSubtractUsersToUpdateFromTotal: false });
    } else {
      this.setState({ shouldSubtractUsersToUpdateFromTotal: true });
    }
    if (this.toogleData[BulkImportUserToogleActionName.DeleteMissingUsers]) {
      this.setState({ shouldSubtractMissingUsersFromTotal: false });
    } else {
      this.setState({ shouldSubtractMissingUsersFromTotal: true });
    }
  };

  renderSelectedFile = () => {
    const { selectedFile } = this.state;
    const { intl } = this.props;

    return (
      selectedFile && (
        <Text p={4} variant="body-1">
          <Text variant="body-2" isBold={true}>
            {intl.formatMessage({
              id: 'BulkImportUserWizard.selectedFile',
              defaultMessage: 'Selected file:',
              description: 'Label for selected file',
            })}
          </Text>
          &nbsp;{this.state.selectedFile.name}
        </Text>
      )
    );
  };

  handleBackToUploadStep = () => {
    this.props.handleSelectedStep(BulkImportUserType.Upload);
  };

  renderUploadContent = () => {
    const defaultAcceptedTypes = [
      FileExtensionType.Xlsx,
      FileExtensionType.Xls,
    ].join(', ');
    const { stepLoading, file, classes, intl } = this.props;

    return (
      <FlexLayout
        space={4}
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
      >
        {stepLoading ? (
          <h4>
            {intl.formatMessage({
              id: 'BulkImportUserWizard.uploadingUsersList',
              defaultMessage: 'Uploading Users List',
              description:
                'Header text while uploading. Example: stepLoading = true',
            })}
          </h4>
        ) : (
          <>
            <Text variant="subtitle">
              {intl.formatMessage({
                id: 'BulkImportUserWizard.submitUsersList',
                defaultMessage: 'Submit Your Users List',
                description: 'Header for upload step',
              })}
            </Text>
            <Text variant="body-1">
              <Link
                variant="body-2"
                color="red-700"
                onClick={this.handleDownloadUserTemplateClick}
              >
                {intl.formatMessage({
                  id: 'BulkImportUserWizard.downloadTemplate',
                  defaultMessage: 'Download the user template',
                  description: 'Link text to download template',
                })}
              </Link>{' '}
              {intl.formatMessage({
                id: 'BulkImportUserWizard.templateDescription',
                defaultMessage:
                  'to organize your users list for import to Evisort.',
                description: 'Description of template purpose',
              })}{' '}
            </Text>
            {this.isBulkUserImportFileAvailable() && this.renderSelectedFile()}
          </>
        )}

        {stepLoading ? (
          <LoadingSpinner size="m" />
        ) : (
          <FileInput
            file={file}
            acceptedFiles={defaultAcceptedTypes}
            onChange={this.handleSelectedFile}
            className={classes.fileInput}
          />
        )}
      </FlexLayout>
    );
  };

  renderSummaryContent = () => {
    const { importSummaryData, stepLoading, intl } = this.props;
    let totalUsers = importSummaryData.results[BulkImportUserSummaryType.Total];
    if (this.state.shouldSubtractMissingUsersFromTotal) {
      totalUsers -=
        importSummaryData.results[BulkImportUserSummaryType.ToBeDeleted];
    }
    if (this.state.shouldSubtractUsersToUpdateFromTotal) {
      totalUsers -=
        importSummaryData.results[BulkImportUserSummaryType.Existing];
    }

    return (
      <FlexLayout
        space={4}
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
      >
        {stepLoading ? (
          <>
            <h4>
              {intl.formatMessage({
                id: 'BulkImportUserWizard.importingUsersList',
                defaultMessage: 'Importing Users List',
                description: 'Header text while importing',
              })}
            </h4>
            <LoadingSpinner size="m" />
          </>
        ) : (
          <SummaryTable
            handleImportResultsErrorFileDownloadClick={
              this.handleImportResultsErrorFileDownloadClick
            }
            importSummaryData={{
              ...importSummaryData.results,
              [BulkImportUserSummaryType.Total]: totalUsers,
            }}
            updateToogleData={this.updateToogleData}
            importActions={this.toogleData}
          />
        )}
      </FlexLayout>
    );
  };

  renderStepActionButton = () => {
    const { step, stepLoading, intl } = this.props;

    return step === BulkImportUserType.Upload
      ? {
          promise: this.handleUserListUploadActionClick,
          text: intl.formatMessage({
            id: 'BulkImportUserWizard.uploadUsersListButton',
            defaultMessage: 'Upload Users List',
            description: 'Button text to upload users list',
          }),
          disabled: !this.isBulkUserImportFileAvailable() || stepLoading,
        }
      : {
          promise: this.handleShowWarningModal,
          text: intl.formatMessage({
            id: 'BulkImportUserWizard.importUsersListButton',
            defaultMessage: 'Import Users List',
            description: 'Button text to import users list',
          }),
          disabled: stepLoading,
        };
  };

  renderStep = () => {
    const { step } = this.props;

    return step === BulkImportUserType.Upload
      ? this.renderUploadContent()
      : this.renderSummaryContent();
  };

  render = () => {
    const { onClose, step, stepLoading, intl } = this.props;
    const { showWarningModal, warningModalProps } = this.state;
    const backButton = step === BulkImportUserType.Summary && {
      promise: this.handleBackToUploadStep,
      text: intl.formatMessage({
        id: 'BulkImportUserWizard.backToUpload',
        defaultMessage: 'Back To Upload',
        description: 'Button text to go back to upload step',
      }),
      disabled: stepLoading,
    };

    return (
      <>
        <WizardPageLayout
          title={{
            text: intl.formatMessage({
              id: 'BulkImportUserWizard.importUsersTitle',
              defaultMessage: 'Import Users',
              description: 'Title for import users wizard',
            }),
          }}
          isContainer
          steps={steps}
          currentStep={step}
          onClose={onClose}
          actionButton={this.renderStepActionButton()}
          backButton={backButton}
          children={
            <div data-testid={UI_USER_IMPORT_WIZARD_TESTID}>
              {this.renderStep()}
            </div>
          }
        />

        {showWarningModal && (
          <BulkUserImportWarningModal {...warningModalProps} />
        )}
      </>
    );
  };
}

BulkImportUserWizard.propTypes = {
  handleDownloadUserTemplateFile: PropTypes.func.isRequired,
  handleImportResultsFileDownload: PropTypes.func.isRequired,
  handleSelectedStep: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  importSummaryData: PropTypes.object,
  onStartImportUserBulkProcessing: PropTypes.func.isRequired,
  onUploadActionClick: PropTypes.func.isRequired,
  step: PropTypes.string,
  stepLoading: PropTypes.bool,
  intl: PropTypes.object.isRequired,
};

export default injectSheet(styles)(
  injectIntl(withBulkImportWarningModal(BulkImportUserWizard)),
);
